import { CollectionMatchParams } from 'utils/collectionPageUtils';
import { StoreType } from '@tock/shared/utils/connect';
import { consumer } from '@tock/coretypes';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';

export const getConsumerPage = (state: StoreType) => state.consumerPage;

/**
 * Given a key, locate the value of a widget modifier with the indicated key
 */
function getWidgetModifierValueFromWidget(modifierKey: string, widget?: consumer.IWidget) {
  return widget?.widgetInfo?.widgetModifier?.find((modifier) => modifier.key === modifierKey)
    ?.value;
}

/**
 * Given a consumer page variant and a widget-type, find the first widget of the given type
 */
function getFirstWidgetOfTypeFromVariant(
  variant: consumer.IConsumerPageVariant[],
  widgetType: string
): consumer.IWidget | undefined {
  for (const v of variant) {
    if (!v.template?.widget) {
      return undefined;
    }
    for (const w of v.template.widget) {
      if (w.widgetInfo?.renderType && w.widgetInfo.renderType === widgetType) {
        return w;
      }
    }
  }
  return undefined;
}

export interface CollectionPage {
  id?: number;
  name?: string;
  dateStart?: string;
  dateEnd?: number;
  decorativeHeroImageUrl?: string;
  decorativeHeroImageUrlDesktop?: string;
  decorativeHeroImageAltText?: string;
  decorativeHexColor?: string;
  metroArea?: consumer.IMetroArea;
  offeringSearchType?: consumer.OfferingSearchType;
  configMode?: 'COLLECTION_DATA' | 'NAMED_CONFIGURATION';
}

/**
 * For a collection at a given slug, return collection-specific information
 */
export const getCollectionPageFromSlug =
  (slug?: string) =>
  (state: StoreType): CollectionPage => {
    if (!slug) {
      return {
        id: undefined,
        name: undefined,
        dateStart: undefined,
        dateEnd: undefined,
        decorativeHeroImageUrl: undefined,
        decorativeHeroImageUrlDesktop: undefined,
        decorativeHeroImageAltText: undefined,
        decorativeHexColor: undefined,
        metroArea: undefined,
        offeringSearchType: undefined,
        configMode: undefined,
      };
    }
    const variant = state.consumerPage.consumerPage[`/c/${slug}`]?.variant;
    const widget = variant && getFirstWidgetOfTypeFromVariant(variant, 'collection-page');

    const widgetContent = widget?.content;
    if (widgetContent?.collectionConfiguration) {
      const collectionConfiguration = widgetContent?.collectionConfiguration;
      const collectionPageConfig = collectionConfiguration?.collectionConfig;

      return {
        id: collectionConfiguration?.id || undefined,
        name: collectionConfiguration?.name || undefined,
        dateStart: collectionPageConfig?.searchWindowStartDate || undefined,
        dateEnd: collectionPageConfig?.searchWindowNumDays || undefined,
        decorativeHeroImageUrl:
          getWidgetModifierValueFromWidget('decorativeHeroImageUrl', widget) || undefined,
        decorativeHeroImageUrlDesktop:
          getWidgetModifierValueFromWidget('decorativeHeroImageUrlDesktop', widget) || undefined,
        decorativeHeroImageAltText:
          getWidgetModifierValueFromWidget('decorativeHeroImageAltText', widget) || undefined,
        decorativeHexColor:
          getWidgetModifierValueFromWidget('decorativeColorHex', widget) || undefined,
        metroArea: collectionPageConfig?.metroArea || undefined,
        offeringSearchType: collectionPageConfig?.offeringSearchType || undefined,
        configMode: 'COLLECTION_DATA',
      };
    } else {
      const namedConfiguration = widgetContent?.namedConfiguration;
      const collectionPageConfig = namedConfiguration?.collectionPageConfig;

      // Ensure that we don't return any null values
      return {
        id: namedConfiguration?.id || undefined,
        name: namedConfiguration?.name || undefined,
        dateStart: collectionPageConfig?.searchWindowStartDate || undefined,
        dateEnd: collectionPageConfig?.searchWindowNumDays || undefined,
        decorativeHeroImageUrl:
          getWidgetModifierValueFromWidget('decorativeHeroImageUrl', widget) || undefined,
        decorativeHeroImageUrlDesktop:
          getWidgetModifierValueFromWidget('decorativeHeroImageUrlDesktop', widget) || undefined,
        decorativeHeroImageAltText:
          getWidgetModifierValueFromWidget('decorativeHeroImageAltText', widget) || undefined,
        decorativeHexColor:
          getWidgetModifierValueFromWidget('decorativeColorHex', widget) || undefined,
        metroArea: collectionPageConfig?.metroArea || undefined,
        offeringSearchType: collectionPageConfig?.offeringSearchType || undefined,
        configMode: 'NAMED_CONFIGURATION',
      };
    }
  };

/**
 * Using the current route info, return the collection page info (if any). Also returns the collectionRoute
 * snippet as it appears in the route, for possible caller convenience.
 */
export const useRoutedCollectionPageInfo = () => {
  const collectionRoute = useRouteMatch<CollectionMatchParams>().params.collection;
  return {
    ...useSelector(getCollectionPageFromSlug(collectionRoute)),
    collectionRoute,
  };
};
